<template>
  <div class="search-result-page" :class="{ 'page-blurred': expandedCourse }">
    <!-- 顶部搜索框 -->
    <div class="search-header">
      <div class="search-container">
        <input v-model="searchQuery" type="text" placeholder="请输入搜索内容..." class="search-input"
          @keyup.enter="handleSearch" />
        <button class="search-btn" @click="handleSearch">
          <span class="search-icon">🔍</span>
        </button>
      </div>
    </div>

    <!-- 切换按钮 -->
    <div class="platform-switch-container">
      <PlatformSwitch v-model="selectedOption" @update:modelValue="handleTypeChange" />
    </div>
    <div v-if="selectedOption === '课程'" class="course-tip">
      课程搜索仅限在浙学用户使用，搜索不到试卷请使用<a href="https://pages.zaizhexue.top/home/web-plugin" target="_blank"
        class="plugin-link">在浙学助手</a> 学习通等其他平台用户可尝试OCS网课助手

    </div>

    <!-- 结果展示区域 -->
    <div class="results-container">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <LoaderCom />

        <!-- <p>正在加载结果...</p> -->
      </div>

      <!-- 错误提示 -->
      <div v-else-if="error" class="error-container">
        <p>{{ error }}</p>
        <button @click="fetchResults" class="retry-btn">重试</button>
      </div>

      <!-- 课程展示组件 -->
      <div v-else-if="selectedOption === '课程'" class="course-section"
        :class="{ 'has-expanded-course': expandedCourse }">
        <CourseList :courses="filteredResults" @course-click="expandCourse" @favorite-click="handleFavorite"
          :expanded-course-id="expandedCourse ? expandedCourse.id : null" />

        <!-- 分页控件 -->
        <div v-if="totalPages > 1" class="pagination">
          <button class="pagination-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
            上一页
          </button>
          <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
          <button class="pagination-btn" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
            下一页
          </button>
        </div>
      </div>

      <!-- 题目展示组件 -->
      <div v-else-if="selectedOption === '题目'" style="width: 100%; height: 100%;">
        <QuestionList :questions="filteredResults" :globalAnswer="dataAnswer" :filterType="filterType"
          :searchQuery="searchQuery" @page-change="handleQuestionPageChange" />
      </div>


    </div>

    <!-- 确保在模板中正确使用了组件 -->
    <CourseDetailModal :course="expandedCourse" :click-position="clickPosition" :animation-active="animationActive"
      :homework-resources="homeworkResources" :exam-resources="examResources" :test-resources="testResources"
      :active-tab="activeTab" :loading="courseLoading" @close="closeExpandedCourse" @transition-end="handleTransitionEnd"
      @resource-click="goToExamPaper" @update:active-tab="activeTab = $event" />


  </div>
</template>

<script>
import PlatformSwitch from '@/components/PlatformSwitch.vue';
import CourseList from '@/components/CourseList.vue';
import QuestionList from '@/components/QuestionList.vue';
import CourseDetailModal from '@/components/CourseDetailModal.vue'; // 导入新组件
import axios from 'axios';
import '@/assets/styles/searchResultPage.css';
import apiConfig from "../config/api.js";
import LoaderCom from '@/components/LoaderCom.vue';

export default {
  components: {
    PlatformSwitch,
    CourseList,
    QuestionList,
    CourseDetailModal, // 注册新组件
    LoaderCom
  },

  data() {
    return {
      searchQuery: '',
      selectedOption: '课程',
      currentPage: 1,
      pageSize: 8, // 修改为每页显示6个课程
      results: [],
      totalItems: 0, // 添加总条目数
      loading: false,
      error: null,
      // 添加筛选和排序状态
      filterType: '全部',
      sortOrder: 'newest', // newest 或 oldest
      // 添加课程展开相关状态
      expandedCourse: null,
      activeTab: 'homework',
      clickPosition: null,
      animationActive: false,
      courseLoading: false, // 添加课程详情加载状态
      // 添加课程资源数据
      homeworkResources: [],
      examResources: [],
      testResources: [],
      // 添加全局答案数据
      dataAnswer: ''
    };
  },

  // 修改计算属性
  computed: {
    filteredResults() {
      // 直接返回当前页的结果，因为分页已经在服务器端处理
      return this.results;
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.pageSize);
    }
  },

  // 修改 activated 生命周期钩子
  activated() {
    // 检查是否从试卷页面返回
    if (this.$route.params.fromExam) {
      // 清除标记，防止重复处理
      this.$route.params.fromExam = false;
      console.log('从试卷页面返回，保持状态');
      return;
    }

    // 从 QuestionDetail 页面返回时恢复状态
    const savedState = sessionStorage.getItem('searchState');
    if (savedState) {
      const state = JSON.parse(savedState);
      this.searchQuery = state.query;
      this.selectedOption = state.option;
      this.currentPage = state.page;
      this.filterType = state.filter;
      this.sortOrder = state.sort;

      // 恢复状态后重新获取数据
      this.fetchPagedData();

      // 清除保存的状态，避免影响其他操作
      sessionStorage.removeItem('searchState');
    }
  },

  mounted() {
    // 确保页面可以滚动
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
  },

  beforeUnmount() {
    // 组件销毁前恢复原始滚动状态
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  },

  created() {
    // 获取当前 URL 中的查询参数
    const query = this.$route.query;

    // 检查是否有保存的状态
    const savedState = sessionStorage.getItem('searchState');

    // 如果 URL 中有查询参数，优先使用 URL 中的参数
    if (query && (query.q || query.type)) {
      this.searchQuery = query.q || '';
      this.selectedOption = query.type || '课程';
      this.currentPage = 1;
      // 清除会话存储中的旧状态，避免干扰
      sessionStorage.removeItem('searchState');
    } else if (savedState) {
      // 如果 URL 中没有查询参数，但有保存的状态，则使用保存的状态
      const state = JSON.parse(savedState);
      this.searchQuery = state.query;
      this.selectedOption = state.option;
      this.currentPage = state.page;
      this.filterType = state.filter;
      this.sortOrder = state.sort;
    }

    this.fetchInitialData(); // 组件创建时获取初始数据
  },


  methods: {
    async expandCourse(course, event) {
      if (event && event.currentTarget) {
        const rect = event.currentTarget.getBoundingClientRect();
        this.clickPosition = {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height
        };
      }

      // 立即显示模态框并设置加载状态
      const year = course.startDate ? course.startDate.substring(0, 4) : '';
      this.expandedCourse = {
        id: course.id,
        title: course.name,
        description: `${year}年课程`,
        papers: [],
        imgUrl: course.imgUrl,
        author: course.teacherName || '未知讲师',
        time: course.startDate || '未知日期'
      };
      
      // 设置加载状态
      this.courseLoading = true;
      this.homeworkResources = [];
      this.examResources = [];
      this.testResources = [];

      // 计算滚动条宽度并补偿
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      
      // 添加类以显示遮罩层
      document.body.classList.add('course-expanded');
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
      // 补偿滚动条宽度
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      this.$nextTick(() => {
        this.animationActive = true;
      });

      // 异步加载试卷数据
      let papers = [];
      
      // 检查缓存
      const cacheKey = `course_papers_${course.id}`;
      const cachedData = localStorage.getItem(cacheKey);

      if (cachedData) {
        try {
          const { data, timestamp } = JSON.parse(cachedData);
          const now = new Date().getTime();
          // 检查是否在30分钟有效期内
          if (now - timestamp < 30 * 60 * 1000) {
            papers = data;
            console.log('使用缓存的试卷数据:', papers.length);
          } else {
            // 清除过期缓存
            localStorage.removeItem(cacheKey);
          }
        } catch (e) {
          // 缓存数据格式错误，清除缓存
          localStorage.removeItem(cacheKey);
        }
      }

      // 如果没有缓存数据，则请求API
      if (papers.length === 0) {
        try {
          // 获取 token 并设置请求头
          const token = localStorage.getItem('token');
          const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

          // 请求试卷列表
          const response = await axios.get(`https://api.csid.cc/papers/${course.id}`, {
            headers: headers
          });

          if (response.data && response.data.data) {
            papers = response.data.data;
            // 缓存数据
            const cacheData = {
              data: papers,
              timestamp: new Date().getTime()
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
            console.log('缓存试卷数据:', papers.length);
          }
        } catch (error) {
          console.error('获取试卷列表失败:', error);
          // 如果请求失败，使用原有的course.papers数据作为备用
          papers = course.papers || [];
        }
      }

      // 更新课程数据和试卷分类
      this.expandedCourse.description = `${year}年课程，包含 ${papers.length} 个试卷`;
      this.expandedCourse.papers = papers;

      // 根据试卷类型分类
      this.homeworkResources = papers
        .filter(paper => paper.type === '作业')
        .map(paper => ({
          id: paper.id,
          title: paper.paperName,
          type: paper.type,
          date: paper.publishTime ? paper.publishTime.split('T')[0] : '',
          endTime: paper.endTime ? paper.endTime.split('T')[0] : ''
        }));

      this.examResources = papers
        .filter(paper => paper.type === '考试')
        .map(paper => ({
          id: paper.id,
          title: paper.paperName,
          type: paper.type,
          date: paper.publishTime ? paper.publishTime.split('T')[0] : '',
          endTime: paper.endTime ? paper.endTime.split('T')[0] : ''
        }));

      this.testResources = papers
        .filter(paper => paper.type === '测验')
        .map(paper => ({
          id: paper.id,
          title: paper.paperName,
          type: paper.type,
          date: paper.publishTime ? paper.publishTime.split('T')[0] : '',
          endTime: paper.endTime ? paper.endTime.split('T')[0] : ''
        }));

      // 数据加载完成，关闭加载状态
      this.courseLoading = false;
    },

    // 修改跳转到试卷页面的方法
    goToExamPaper(item) {
      // 保存当前状态到 sessionStorage
      sessionStorage.setItem('searchState', JSON.stringify({
        query: this.searchQuery,
        option: this.selectedOption,
        page: this.currentPage,
        filter: this.filterType,
        sort: this.sortOrder
      }));

      console.log('跳转到试卷页面，试卷ID:', item.id);

      // 构建路由路径，使用试卷的实际ID
      this.$router.push({
        name: 'ExamPaper',
        params: {
          id: item.id,
          fromDashboard: 'false',
          title: item.title,
          type: item.type,
          course: this.expandedCourse.title,
          fromSearch: true
        }
      });
    },

    // 格式化日期显示
    formatDate(dateString) {
      if (!dateString) return '未知日期';

      // 如果日期包含T，说明是ISO格式，需要分割
      if (dateString.includes('T')) {
        dateString = dateString.split('T')[0];
      }

      // 将YYYY-MM-DD格式转换为YYYY年MM月DD日
      const parts = dateString.split('-');
      if (parts.length === 3) {
        return `${parts[0]}年${parts[1]}月${parts[2]}日`;
      }

      return dateString;
    },

    // 关闭展开的课程详情
    // 修改关闭方法
    closeExpandedCourse() {
      this.animationActive = false;
      this.expandedCourse = null;
      // 不要立即移除样式和补偿，等待过渡结束
    },

    // 修改过渡结束的处理方法
    handleTransitionEnd() {
      // 过渡结束后执行清理操作
      if (!this.expandedCourse) {
        document.body.classList.remove('course-expanded');
        document.body.style.overflow = 'auto';
        document.documentElement.style.overflow = 'auto';
        // 移除滚动条宽度补偿
        document.body.style.paddingRight = '';
      }
    },

    // 添加搜索处理方法
    async handleSearch() {
      if (!this.searchQuery.trim()) {
        return;
      }

      // 更新 URL，但不触发路由变化
      this.$router.replace({
        query: {
          ...this.$route.query,
          q: this.searchQuery,
          type: this.selectedOption
        }
      }, () => { }, // 空函数，避免导航完成回调
        { shallow: true }); // 浅路由导航，不触发组件重新渲染

      this.currentPage = 1; // 重置到第一页
      await this.fetchPagedData();
    },

    // 添加获取分页数据的方法
    async fetchPagedData() {
      this.loading = true;
      this.error = null;

      try {
        const params = {
          query: this.searchQuery || '全部',
          type: this.selectedOption,
          page: this.currentPage,
          pageSize: this.pageSize,
          filter: this.filterType !== '全部' ? this.filterType : undefined,
          sort: this.sortOrder
        };

        // 生成缓存键
        const cacheKey = `search_results_${JSON.stringify(params)}`;

        // 检查缓存
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
          const { data, timestamp } = JSON.parse(cachedData);
          const now = new Date().getTime();
          // 检查是否在5分钟有效期内
          if (now - timestamp < 5 * 60 * 1000) {
            // 使用缓存数据
            this.handleSearchResponse(data);
            this.loading = false;
            return;
          } else {
            // 清除过期缓存
            localStorage.removeItem(cacheKey);
          }
        }

        // 获取 token 并设置请求头
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

        const response = await axios.get(`https://api.csid.cc/search`, {
          params: params,
          headers: headers
        });

        console.log('API响应:', response.data);

        if (response.data.success) {
          // 缓存结果，包含时间戳
          const cacheData = {
            data: response.data,
            timestamp: new Date().getTime()
          };
          localStorage.setItem(cacheKey, JSON.stringify(cacheData));

          // 处理响应数据
          this.handleSearchResponse(response.data);
        } else {
          this.error = response.data.message || '获取数据失败，请重试';
          this.results = [];
          this.totalItems = 0;
        }
      } catch (error) {
        console.error('获取数据错误:', error);
        this.error = '网络错误，请检查连接后重试';
        this.results = [];
        this.totalItems = 0;
      } finally {
        this.loading = false;
      }
    },

    // 添加处理响应数据的方法
    handleSearchResponse(responseData) {
      if (responseData.data) {
        // 获取全局答案
        this.dataAnswer = responseData.data.answer || '';
        console.log('获取到全局答案:', this.dataAnswer);

        // 根据不同的选项类型处理数据
        if (this.selectedOption === '题目') {
          this.results = responseData.data.paperSubjects || [];
          if (responseData.data.pagination) {
            this.totalItems = responseData.data.pagination.total || 0;
            this.currentPage = responseData.data.pagination.page || 1;
          } else {
            this.totalItems = this.results.length;
          }
        } else if (this.selectedOption === '课程') {
          this.results = responseData.data.courses || [];
          if (responseData.data.pagination) {
            this.totalItems = responseData.data.pagination.total || 0;
          } else {
            this.totalItems = this.results.length;
          }
        }

        console.log('处理后的结果数量:', this.results.length);
      } else {
        console.error('响应数据结构不符合预期:', responseData);
        this.results = [];
        this.totalItems = 0;
        this.error = '数据格式错误，请联系管理员';
      }
    },

    // ... 修改获取初始数据的方法
    async fetchInitialData() {
      await this.fetchPagedData();
    },

    // 修改 handleTypeChange 方法
    async handleTypeChange() {
      // 重置页码到第一页
      this.currentPage = 1;
      await this.fetchPagedData(); // 切换类型时重新获取数据
    },

    // 修改重试方法
    async fetchResults() {
      await this.fetchPagedData();
    },

    // 修改分页方法
    async changePage(page) {
      if (page < 1 || page > this.totalPages) {
        return;
      }
      this.currentPage = page;
      await this.fetchPagedData(); // 切换页码时重新获取数据

      // 滚动到页面顶部
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },

    // 修改筛选方法
    async applyFilter(type) {
      this.filterType = type;
      // 重置页码到第一页
      this.currentPage = 1;
      await this.fetchPagedData(); // 应用筛选时重新获取数据
    },

    // 修改排序方法
    async applySorting(order) {
      this.sortOrder = order;
      // 重置页码到第一页
      this.currentPage = 1;
      await this.fetchPagedData(); // 应用排序时重新获取数据
    },

    // 添加打开题集页面的方法
    openCollection(collection) {
      // 保存当前状态到 sessionStorage
      sessionStorage.setItem('searchState', JSON.stringify({
        query: this.searchQuery,
        option: this.selectedOption,
        page: this.currentPage,
        filter: this.filterType,
        sort: this.sortOrder
      }));

      // 使用正确的路由名称
      const routePath = this.$router.resolve({
        name: 'CollectionDetail',
        params: {
          id: collection.id
        },
        query: {
          name: collection.name,
          fromSearch: true
        }
      });

      // 在新窗口中打开
      window.open(routePath.href, '_blank');
    },

    // 查看题目详情
    viewQuestionDetail(question) {
      console.log('查看题目详情:', question);

      // 保存当前状态到 sessionStorage
      sessionStorage.setItem('searchState', JSON.stringify({
        query: this.searchQuery,
        option: this.selectedOption,
        page: this.currentPage,
        filter: this.filterType,
        sort: this.sortOrder
      }));

      // 跳转到题目详情页面
      this.$router.push({
        name: 'QuestionDetail',
        params: {
          id: question.uuid,
        },
        query: {
          courseId: question.courseId,
          paperId: question.paperId,
          fromSearch: true
        }
      });
    },

    // 处理题目分页变化
    handleQuestionPageChange(page) {
      console.log('题目分页变化:', page);
      // 如果需要从服务器获取分页数据，可以在这里处理
      // 否则，分页逻辑已经在 QuestionList 组件内部处理
    },

    // 添加收藏处理方法
    async handleFavorite(courseId) {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          // alert('请先登录后再收藏课程');
          return;
        }

        // 找到对应的课程
        const course = this.results.find(c => c.id === courseId);
        if (!course) return;

        if (course.isFavorite) {
          // 如果已收藏，发送取消收藏请求
          const response = await axios.delete(`${apiConfig.appURL}/api/favorites/remove`, {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            data: { courseId }
          });

          if (response.data.success) {
            course.isFavorite = false;
            // alert('取消收藏成功');
          }
        } else {
          // 如果未收藏，发送收藏请求
          const response = await axios.post(`${apiConfig.appURL}/api/favorites/add`,
            { courseId },
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (response.data.success) {
            course.isFavorite = true;
            // alert(response.data.message);
          }
        }
      } catch (error) {
        console.error('收藏操作失败:', error);
        alert('操作失败，请稍后重试');
      }
    },
  }
};
</script>

<style scoped>
/* 添加样式 */
</style>
